import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Chip,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Alert,
  Tooltip,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  RequestQuote as QuoteIcon,
  CheckCircle as CompleteIcon,
  Cancel as CancelIcon,
  ChatBubbleOutline as ChatIcon,
} from '@mui/icons-material';
import { providerApi } from '../../services/api';
import { FEATURES, hasFeature } from '../../plan/plan';
import { formatPrice, resolveOrderCurrency } from '../../utils/currencyUtils';
import { orderStatusLabel, completeAwaitsCapture, completeAwaitsEnd, cancellationCompleted, isTerminal } from '../../utils/orderStatusLabel';

const STATUS_COLORS = {
  CREATED: 'default',
  ACTIVE: 'info',
  DISCUSSION: 'info',
  CONFIRMED: 'primary',
  PAID: 'warning',
  COMPLETED: 'success',
  CANCELLED_BY_USER: 'error',
  CANCELLED_BY_PROVIDER: 'error',
};


/**
 * The server now sends {code, field, message} for proposal errors (design §J), so the composer
 * reads `code` and highlights `field` — it never matches on message text.
 *
 * PRO_REQUIRED is deliberately absent: it replaces the composer with the upgrade CTA rather than
 * showing a field error. The provider did nothing wrong.
 */
// Exported so a test can assert every code maps to a key that actually resolves. An unmapped
// code is not an error — applyProposalError falls back to the server's own message — but a
// mapped code pointing at a MISSING key renders the raw i18n path to the provider.
export const PROPOSAL_ERROR_COPY = {
  AVAILABILITY_LOST: 'orderChat.errAvailabilityLost',
  BOOKING_WINDOW_TOO_SHORT: 'orderChat.errWindowTooShort',
  // A DIFFERENT failure from the one above: reporting a ceiling as "too close to the start"
  // misattributes it, which is the reporting bug the server-side split corrects.
  BOOKING_TOO_FAR_AHEAD: 'orderChat.errTooFarAhead',
  INVALID_SERVICE_TIME: 'orderChat.errInvalidServiceTime',
  INVALID_SERVICE: 'orderChat.errInvalidService',
  INVALID_DOG: 'orderChat.errInvalidDog',
};

/**
 * Last-resort value for the booking ceiling when the server did not send one.
 *
 * `maxLeadDays` is derived from two config values on the server, so a copy here drifts silently
 * the moment either is tuned — and it was baked into the en and he sentences as the literal 27,
 * where even a careful reader would not think to look. The server now sends the real number on
 * the rejection; this is only for a build that predates that.
 */
export const BOOKING_MAX_LEAD_DAYS_FALLBACK = 27;

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return sameDay ? time : `${d.toLocaleDateString()} ${time}`;
};

const serviceTypeLabel = (order, t) => {
  if (order?.orderType === 'STORE') return t('orders.storeOrder');
  // Placeholder until the provider proposes (design §A).
  if (order?.orderType === 'SERVICE' && !order?.hasProposal) return t('orders.serviceOrder');
  return order?.dogServiceType
    ? t('services.type' + order.dogServiceType, { defaultValue: order.dogServiceType.replace(/_/g, ' ') })
    : t('orders.serviceOrder');
};

const OrderChatDialog = ({ open, order, providerData, onClose, onUpdated, onUpgradeClick }) => {
  const { t } = useTranslation();
  const orderId = order?.id;
  const [current, setCurrent] = useState(order);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [proposal, setProposal] = useState({ providerDogServiceId: '', date: '', time: '', numOfDays: '1', dogIds: [], note: '', amount: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const scrollRef = useRef(null);

  const myUserId = providerData?.user?.id != null ? String(providerData.user.id) : null;
  const currencyCode = resolveOrderCurrency(current);

  /** design §A: false means dogServiceType/requestDate/numOfDays are placeholder padding. */
  const hasProposal = Boolean(current?.hasProposal);

  /**
   * The composer's candidate set is `eligibleDogs`, NOT `order.dogs`.
   *
   * submitQuote REPLACES order.dogs with the selection, so reading only that would make a dog
   * excluded by the previous proposal unreachable forever — and a dog the customer acquired
   * since would never appear. The server sends eligibleDogs only to the selected provider, and
   * only while a proposal is still possible.
   */
  const eligibleDogs = current?.eligibleDogs?.length ? current.eligibleDogs : (current?.dogs || []);
  // The fallback above is for an older server only. When eligibleDogs IS present it is the
  // authoritative candidate set, and it is a superset of the current selection.

  /** A shop catalogue is not a bookable service, and an unpriced one is not sellable. */
  const sellableServices = (providerData?.services || []).filter(
    (svc) => svc.dogServiceType !== 'STORE' && Number(svc.price) > 0,
  );

  /** Labelled, not converted: the SERVER owns the zone (design §B). */
  const bookingZone = providerData?.effectiveTimeZone || providerData?.timeZone || '';

  /** Sending a proposal is Pro; reading the thread and chatting stay free (design §B). */
  const canSendProposal = hasFeature(providerData, FEATURES.QUOTE);

  /** Pre-filled from the previous proposal, so revising is an edit rather than a re-entry. */
  const openComposer = () => {
    // The SERVER's provider-local wall clock, used verbatim. NOT derived from serviceStartAt:
    // that is an INSTANT, and `new Date(...).toISOString()` renders it in UTC — so revising a
    // 14:30 Jerusalem proposal pre-filled 11:30 and resubmitted it as 11:30 LOCAL, silently
    // moving the booking three hours. The one conversion clients must never do.
    const local = hasProposal ? (current?.serviceStartLocal || '') : '';
    setFieldErrors({});
    setProposal({
      providerDogServiceId: current?.providerDogService?.id || sellableServices[0]?.id || '',
      date: local.slice(0, 10),
      time: local.slice(11, 16),
      numOfDays: hasProposal && current?.numOfDays ? String(current.numOfDays) : '1',
      dogIds: (current?.dogs || []).map((d) => d.id),
      note: current?.moreInfo || '',
      amount: current?.acceptedQuoteAmount != null ? String(current.acceptedQuoteAmount) : '',
    });
    setQuoteOpen(true);
  };

  useEffect(() => { setCurrent(order); }, [order]);

  const loadMessages = useCallback(async () => {
    if (!orderId) return;
    try {
      const msgs = await providerApi.getOrderMessages(orderId);
      setMessages(Array.isArray(msgs) ? msgs : []);
    } catch (e) {
      // keep whatever we have; surface nothing (polling will retry)
    } finally {
      setLoadingMessages(false);
    }
  }, [orderId]);

  const refreshOrder = useCallback(async () => {
    if (!orderId) return;
    try {
      const fresh = await providerApi.getOrder(orderId);
      if (fresh) setCurrent(fresh);
    } catch (_) { /* non-fatal */ }
  }, [orderId]);

  // Initial load + light polling so the provider sees the customer's replies
  useEffect(() => {
    if (!open) return undefined;
    setLoadingMessages(true);
    loadMessages();
    const iv = setInterval(loadMessages, 5000);
    return () => clearInterval(iv);
  }, [open, loadMessages]);

  // Keep the thread scrolled to the latest message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const status = current?.status;
  const orderType = current?.orderType || 'SERVICE';
  const isClosed = isTerminal(current);
  // The server decides when the chat goes read-only (chatClosesAt, stamped at closing); a closed
  // order keeps its chat open for a few days first.
  const chatClosed = current?.chatClosed === true;

  // quoteStatus, not just business status: acceptance deliberately leaves the order in
  // DISCUSSION until a verified authorization (contract §6), so checking status alone still
  // offered the composer on an accepted booking — and submitQuote answers 409 for it.
  const canQuote = orderType === 'SERVICE' &&
    !isClosed && status !== 'CONFIRMED' &&
    current?.quoteStatus !== 'ACCEPTED';
  // SERVICE only: STORE orders are shipped / delivered from the Orders list (complete-job answers
  // 400 for them), so the chat offers no Complete Job on a store order.
  const canComplete = orderType === 'SERVICE' && status === 'CONFIRMED';
  const canCancel = !isClosed;
  // Settled directly with the customer: no capture to wait for; the provider marks it paid.
  const offline = orderType === 'SERVICE' && current?.paymentStatus === 'OFFLINE' &&
    (status === 'CONFIRMED' || status === 'COMPLETED');
  // Shown but disabled until capture: completion needs a SETTLED payment server-side. Both
  // modes are additionally refused before the expected end (SERVICE_NOT_FINISHED).
  const awaitsCapture = completeAwaitsCapture(current);
  const awaitsEnd = completeAwaitsEnd(current);
  const completeLocked = awaitsCapture || awaitsEnd;
  const completeAfterEndText = awaitsEnd
    ? t('orders.completeAfterEnd', { date: new Date(current.expectedCompletionAt).toLocaleString() })
    : '';

  // A payment operation may be in flight from ANOTHER session — the customer's app, or this
  // dashboard in a second tab. `actionLoading` only knows about this component's own request, so
  // on its own it would happily offer complete/cancel over a live operation lease and the server
  // would answer 409 + Retry-After. The server-computed flag is the only view that spans
  // sessions (contract §6).
  const operationInProgress = current?.paymentOperationInProgress === true;
  const actionsBlocked = actionLoading || sending || operationInProgress;

  const handleSend = async () => {
    const text = messageText.trim();
    if (!text || sending) return;
    setSending(true);
    setError('');
    try {
      await providerApi.sendOrderMessage(orderId, text, 'TEXT');
      setMessageText('');
      await Promise.all([loadMessages(), refreshOrder()]);
      onUpdated && onUpdated();
    } catch (e) {
      if (e.response?.data?.code === 'ORDER_CHAT_CLOSED') {
        // Closed while the dialog was open: reload so the input disables and the notice shows.
        await refreshOrder();
      } else {
        setError(e.response?.data?.message || t('orderChat.sendFailed'));
      }
    } finally {
      setSending(false);
    }
  };

  const handleSendQuote = async () => {
    const price = parseFloat(proposal.amount);
    if (isNaN(price) || price <= 0) {
      setFieldErrors({ amount: t('orderChat.invalidPrice') });
      return;
    }
    if (!proposal.providerDogServiceId) {
      setFieldErrors({ providerDogServiceId: t('orderChat.pickAService') });
      return;
    }
    if (!proposal.date || !proposal.time) {
      setFieldErrors({ serviceStartLocal: t('orderChat.pickADateAndTime') });
      return;
    }
    if (proposal.dogIds.length === 0) {
      setFieldErrors({ dogIds: t('orderChat.pickADog') });
      return;
    }

    setSending(true);
    setError('');
    setFieldErrors({});
    try {
      await providerApi.sendQuote(orderId, {
        providerDogServiceId: Number(proposal.providerDogServiceId),
        amount: price,
        // A BARE local wall clock. Never `new Date(...).toISOString()` — that converts to UTC
        // using the BROWSER's zone, which is not the provider's business zone, and the server
        // rejects an offset-bearing value rather than silently reinterpreting it.
        serviceStartLocal: `${proposal.date}T${proposal.time}:00`,
        numOfDays: Number(proposal.numOfDays) || 1,
        dogIds: proposal.dogIds,
        body: proposal.note || undefined,
      });
      setQuoteOpen(false);
      await Promise.all([loadMessages(), refreshOrder()]);
      onUpdated && onUpdated();
    } catch (e) {
      applyProposalError(e);
    } finally {
      setSending(false);
    }
  };

  /** Reads `code`, highlights `field`. Never matches on message text (design §J). */
  const applyProposalError = (e) => {
    const { code, field, message, maxLeadDays } = e.response?.data || {};
    const key = PROPOSAL_ERROR_COPY[code];
    if (!key) {
      // An unrecognised code degrades to the server's own message, so a new one is readable
      // rather than silent.
      setError(message || t('orderChat.quoteFailed'));
      return;
    }
    // Sent as a string: the proposal error body is a flat string map on the wire.
    const days = Number(maxLeadDays) || BOOKING_MAX_LEAD_DAYS_FALLBACK;
    const text = t(key, { days });
    if (field) setFieldErrors({ [field]: text });
    else setError(text);
  };

  const handleAction = async (action) => {
    setActionLoading(true);
    setError('');
    setNotice('');
    try {
      const updated = await providerApi.performOrderAction(orderId, action);
      if (updated) setCurrent(updated);
      if (action === 'cancel' && !cancellationCompleted(updated)) {
        setNotice(t('orders.cancellationPending'));
      }
      await Promise.all([loadMessages(), refreshOrder()]);
      onUpdated && onUpdated();
    } catch (e) {
      const code = e.response?.data?.code;
      const typed = {
        SERVICE_ALREADY_STARTED: 'orders.serviceAlreadyStarted',
        SERVICE_NOT_FINISHED: 'orders.serviceNotFinished',
        BOOKING_DISPUTED: 'orders.bookingDisputed',
      }[code];
      setError(typed
        ? t(typed)
        : (e.response?.data?.message || (action === 'complete' ? t('orderChat.completeFailed') : t('orderChat.cancelFailed'))));
    } finally {
      setActionLoading(false);
    }
  };

  /** OFFLINE only: records the provider received payment directly from the customer. */
  const handleMarkPaid = async () => {
    setActionLoading(true);
    setError('');
    try {
      const updated = await providerApi.markOrderPaid(orderId);
      if (updated) setCurrent(updated);
      onUpdated && onUpdated();
    } catch (e) {
      setError(e.response?.data?.code ? t('orders.markPaidFailed') : (e.response?.data?.message || t('orders.markPaidFailed')));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, height: '85vh' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ textTransform: 'capitalize' }}>
                {serviceTypeLabel(current, t)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Chip label={t('orders.status' + status, { defaultValue: status })} color={STATUS_COLORS[status] || 'default'} size="small" />
                <Typography variant="body2" color="text.secondary">
                  {t('orderChat.orderNumber')} #{orderId}
                </Typography>
                {current?.price != null && (
                  <Typography variant="body2" fontWeight={700} color="primary">
                    {formatPrice(current.price, currencyCode)}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box>
              <Tooltip title={t('orderChat.refresh')}>
                <IconButton size="small" onClick={() => { loadMessages(); refreshOrder(); }}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          {/* Customer + request summary */}
          <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              {t('orders.customerName')}: <b>{current?.user?.name || t('orders.na')}</b>
            </Typography>
            {/*
              design §A: on an INQUIRY these are PLACEHOLDERS — an arbitrary service, today's
              date, one day — seeded only because the columns are NOT NULL. Rendering them shows
              the provider a "requirement" the customer never expressed.
            */}
            {hasProposal && current?.requestDate && (
              <Typography variant="caption" color="text.secondary">
                {t('orders.colDate')}: <b>{new Date(current.requestDate).toLocaleDateString()}</b>
              </Typography>
            )}
            {hasProposal && orderType === 'SERVICE' && current?.numOfDays != null && (
              <Typography variant="caption" color="text.secondary">
                <b>{t('orders.durationDays', { count: current.numOfDays })}</b>
              </Typography>
            )}
            {!hasProposal && orderType === 'SERVICE' && (
              <Typography variant="caption" color="text.secondary">
                {t('orderChat.waitingForYourProposal')}
              </Typography>
            )}
          </Box>
          {current?.moreInfo ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {t('orderChat.note')}: {current.moreInfo}
            </Typography>
          ) : null}
        </DialogTitle>

        <Divider />

        {/*
          The record of what was sent. The composer below prefills from the same proposal, but
          that is an edit form - reading a sent proposal used to mean opening it.
          `serviceStartLocal` is the server's wall clock in the SERVICE's zone: render it
          verbatim, never through `new Date(...)`, or a provider whose browser sits in another
          zone sees a different hour than the customer was shown.
        */}
        {hasProposal && (current?.quoteStatus === 'QUOTED' || current?.quoteStatus === 'ACCEPTED') && (
          <Box sx={{ mx: 2, mt: 1.5, p: 1.5, bgcolor: 'success.light', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t('orderChat.sentProposalTitle')}
            </Typography>
            {current?.serviceStartLocal && (
              <Typography variant="caption" sx={{ display: 'block' }}>
                {t('orderChat.sentProposalWhen')}: <b>{current.serviceStartLocal.replace('T', ' ').slice(0, 16)}</b>
              </Typography>
            )}
            {current?.numOfDays != null && (
              <Typography variant="caption" sx={{ display: 'block' }}>
                {t('orderChat.sentProposalDuration')}: <b>{t('orders.durationDays', { count: current.numOfDays })}</b>
              </Typography>
            )}
            {current?.acceptedQuoteAmount != null && (
              <Typography variant="caption" sx={{ display: 'block' }}>
                {t('orderChat.sentProposalPrice')}: <b>{current.acceptedQuoteAmount}</b>
              </Typography>
            )}
            {current?.moreInfo && (
              <Typography variant="caption" sx={{ display: 'block' }}>
                {t('orderChat.note')}: {current.moreInfo}
              </Typography>
            )}
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mt: 0.5 }}>
              {orderStatusLabel(current, t)}
            </Typography>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mx: 2, mt: 1 }} onClose={() => setError('')}>{error}</Alert>}
        {notice && <Alert severity="info" sx={{ mx: 2, mt: 1 }} onClose={() => setNotice('')}>{notice}</Alert>}

        {/* Message thread */}
        <DialogContent ref={scrollRef} sx={{ bgcolor: '#f8f9fb', py: 2 }}>
          {loadingMessages && messages.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ textAlign: 'center', color: 'text.disabled', py: 6 }}>
              <ChatIcon sx={{ fontSize: 44, opacity: 0.4, mb: 1 }} />
              <Typography variant="body2">{t('orderChat.emptyThread')}</Typography>
            </Box>
          ) : (
            messages.map((m, idx) => {
              const mine = myUserId != null && String(m.senderId) === myUserId;
              const isQuote = m.type === 'QUOTE' || m.type === 'QUOTE_REQUEST' || m.type === 'BOOKING' || m.type === 'CANCEL';
              return (
                <Box key={m.id ?? idx} sx={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', mb: 1.25 }}>
                  <Box
                    sx={{
                      maxWidth: '78%',
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: mine ? 'primary.main' : '#fff',
                      color: mine ? '#fff' : 'text.primary',
                      border: isQuote ? '1.5px solid' : '1px solid',
                      borderColor: isQuote ? (mine ? 'rgba(255,255,255,0.6)' : 'primary.main') : 'divider',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                    }}
                  >
                    {isQuote && (
                      <Typography variant="caption" sx={{ fontWeight: 700, opacity: 0.8, display: 'block', textTransform: 'capitalize' }}>
                        {String(m.type).replace(/_/g, ' ').toLowerCase()}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {m.body}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.6, display: 'block', textAlign: 'right', mt: 0.25 }}>
                      {formatTime(m.sentAt)}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </DialogContent>

        <Divider />

        {/* Provider actions */}
        {operationInProgress && (
          <Alert severity="info" sx={{ mx: 2, mb: 1 }}>
            {t('orderChat.paymentProcessing')}
          </Alert>
        )}
        {offline && (
          <Alert severity="info" sx={{ mx: 2, mb: 1 }}>
            {t('orders.offlineSettlementHint')}
            {canComplete && awaitsEnd && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {completeAfterEndText}
              </Typography>
            )}
            <Box sx={{ mt: 1 }}>
              {current?.offlinePaidAt ? (
                <Typography variant="body2">
                  {t('orders.paidOn', { date: new Date(current.offlinePaidAt).toLocaleDateString() })}
                </Typography>
              ) : (
                <Button size="small" variant="outlined" disabled={actionLoading} onClick={handleMarkPaid}>
                  {t('orders.markAsPaid')}
                </Button>
              )}
            </Box>
          </Alert>
        )}
        {/* The expected-end reason wins over the capture one (as in Flutter and the list tooltip). */}
        {!offline && canComplete && awaitsCapture && !awaitsEnd && !operationInProgress && (
          <Alert severity="info" sx={{ mx: 2, mb: 1 }}>
            {t('orders.completeAfterCapture')}
          </Alert>
        )}
        {!offline && canComplete && awaitsEnd && !operationInProgress && (
          <Alert severity="info" sx={{ mx: 2, mb: 1 }}>
            {completeAfterEndText}
          </Alert>
        )}
        {(canQuote || canComplete || canCancel) && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', px: 2, pt: 1.5 }}>
            {canQuote && !canSendProposal && (
              /*
                design §B: the thread, its history and the chat composer all stay fully usable —
                only the "send a proposal" entry point is replaced. The server's 403 PRO_REQUIRED
                remains the backstop; this is UX.
              */
              <Button size="small" variant="outlined" color="secondary" onClick={onUpgradeClick}>
                {t('orderChat.upgradeToPropose')}
              </Button>
            )}
            {canQuote && canSendProposal && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<QuoteIcon />}
                onClick={openComposer}
                disabled={actionsBlocked}
              >
                {hasProposal ? t('orderChat.sendNewProposal') : t('orderChat.giveQuote')}
              </Button>
            )}
            {canComplete && (
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <CompleteIcon />}
                onClick={() => handleAction('complete')}
                disabled={actionsBlocked || completeLocked}
              >
                {t('orderChat.completeJob')}
              </Button>
            )}
            {canCancel && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => handleAction('cancel')}
                disabled={actionsBlocked}
              >
                {t('orders.cancelOrder')}
              </Button>
            )}
          </Box>
        )}

        {isClosed && (chatClosed || current?.chatClosesAt) && (
          <Alert severity="info" sx={{ mx: 2, mt: 1 }}>
            {chatClosed
              ? t('orderChat.chatClosed')
              : t('orderChat.chatClosesOn', { date: new Date(current.chatClosesAt).toLocaleDateString() })}
          </Alert>
        )}

        {/* Message input */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, p: 2 }}>
          <TextField
            fullWidth
            size="small"
            multiline
            maxRows={4}
            placeholder={t('orderChat.messagePlaceholder')}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            disabled={chatClosed}
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={sending || chatClosed || !messageText.trim()}
            sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'action.disabledBackground' } }}
          >
            {sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          </IconButton>
        </Box>
      </Dialog>

      {/* Quote price prompt */}
      <Dialog open={quoteOpen} onClose={() => setQuoteOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle>{t('orderChat.giveQuote')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('orderChat.quoteExplainer')}
          </Typography>

          <TextField
            select
            fullWidth
            margin="dense"
            label={t('orderChat.service')}
            value={proposal.providerDogServiceId}
            onChange={(e) => setProposal((p) => ({ ...p, providerDogServiceId: e.target.value }))}
            error={Boolean(fieldErrors.providerDogServiceId)}
            helperText={fieldErrors.providerDogServiceId || ''}
          >
            {sellableServices.map((svc) => (
              <MenuItem key={svc.id} value={svc.id}>
                {t('services.type' + svc.dogServiceType, {
                  defaultValue: String(svc.dogServiceType).replace(/_/g, ' '),
                })}{' — '}{formatPrice(svc.price, svc.currencyCode || currencyCode)}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <TextField
              fullWidth
              margin="dense"
              type="date"
              label={t('orderChat.date')}
              InputLabelProps={{ shrink: true }}
              value={proposal.date}
              onChange={(e) => setProposal((p) => ({ ...p, date: e.target.value }))}
              error={Boolean(fieldErrors.serviceStartLocal)}
            />
            <TextField
              fullWidth
              margin="dense"
              type="time"
              label={t('orderChat.time')}
              InputLabelProps={{ shrink: true }}
              value={proposal.time}
              onChange={(e) => setProposal((p) => ({ ...p, time: e.target.value }))}
              error={Boolean(fieldErrors.serviceStartLocal)}
            />
          </Box>
          {fieldErrors.serviceStartLocal && (
            <Typography variant="caption" color="error">{fieldErrors.serviceStartLocal}</Typography>
          )}
          {/*
            The zone is the SERVER's to resolve (design §B); this is a plain local picker with a
            label, not a conversion. Shown so a provider whose business zone is not their own can
            see which one they are booking in.
          */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {t('orderChat.timesShownIn', { zone: bookingZone })}
          </Typography>

          <TextField
            fullWidth
            margin="dense"
            type="number"
            inputProps={{ min: 1 }}
            label={t('orderChat.numOfDays')}
            value={proposal.numOfDays}
            onChange={(e) => setProposal((p) => ({ ...p, numOfDays: e.target.value }))}
          />

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('orderChat.dogsCovered')}
          </Typography>
          <FormGroup row>
            {eligibleDogs.map((dog) => (
              <FormControlLabel
                key={dog.id}
                label={dog.name}
                control={
                  <Checkbox
                    checked={proposal.dogIds.includes(dog.id)}
                    onChange={(e) => setProposal((p) => ({
                      ...p,
                      dogIds: e.target.checked
                        ? [...p.dogIds, dog.id]
                        : p.dogIds.filter((id) => id !== dog.id),
                    }))}
                  />
                }
              />
            ))}
          </FormGroup>
          {fieldErrors.dogIds && (
            <Typography variant="caption" color="error">{fieldErrors.dogIds}</Typography>
          )}

          <TextField
            fullWidth
            margin="dense"
            multiline
            minRows={2}
            label={t('orderChat.note')}
            value={proposal.note}
            onChange={(e) => setProposal((p) => ({ ...p, note: e.target.value }))}
          />

          <TextField
            fullWidth
            margin="dense"
            type="number"
            label={t('services.price')}
            value={proposal.amount}
            onChange={(e) => setProposal((p) => ({ ...p, amount: e.target.value }))}
            error={Boolean(fieldErrors.amount)}
            helperText={fieldErrors.amount || ''}
            InputProps={{ startAdornment: <span style={{ marginRight: 6 }}>{formatPrice(0, currencyCode).replace(/[\d.,\s]/g, '')}</span> }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setQuoteOpen(false)} disabled={sending}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSendQuote} disabled={sending}>
            {sending ? <CircularProgress size={20} color="inherit" /> : t('orderChat.sendProposal')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrderChatDialog;
